import { jsPDF } from 'jspdf';
import { drawYourTourHeader, drawYourTourFooter } from './yourTourPdfBranding';
import { drawMstHeader, drawMstFooter } from './mstPdfBranding';
import { buildMealPlan } from './mealPlan';
import { buildMstIncludedBullets } from './generateYourTourQuoteLetterPdf';

const CURRENCY_SYMBOLS = { GBP: '£', EUR: '€', USD: '$' };

const BLACK = [17, 24, 39];
const GRAY  = [107, 114, 128];
const LGRAY = [229, 231, 235];

function fmt(n, sym = '£') {
  if (n == null || n === '') return '—';
  return `${sym}${Number(n).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function formatDate(str) {
  if (!str) return '—';
  const [y, m, d] = str.split('-');
  return `${d}/${m}/${y}`;
}

function addDays(dateStr, days) {
  const d = new Date(dateStr);
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
}

function subtractMonths(dateStr, months) {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  d.setMonth(d.getMonth() - months);
  return d.toISOString().slice(0, 10);
}

function midDateStr(a, b) {
  if (!a || !b) return '';
  return new Date(Math.round((new Date(a).getTime() + new Date(b).getTime()) / 2))
    .toISOString().slice(0, 10);
}

function setColor(doc, rgb) { doc.setTextColor(rgb[0], rgb[1], rgb[2]); }
function setFill(doc, rgb)  { doc.setFillColor(rgb[0], rgb[1], rgb[2]); }
function setDraw(doc, rgb)  { doc.setDrawColor(rgb[0], rgb[1], rgb[2]); }

/**
 * Generates the parent confirmation letter as a PDF — Your Tour, or MST
 * (MST-branded, with the included list built from the tour's quote rather
 * than Your Tour's fixed package). Returns base64-encoded PDF string.
 */
export function generateConfirmationLetterPdf(request, logoDataUrl = null, crestDataUrl = null) {
  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });

  const pageW = 210;
  const pageH = 297;
  const marginL = 18;
  const marginR = 18;
  const contentW = pageW - marginL - marginR;

  const quoteDetails = request.quoteDetails || {};
  const addons = request.selectedAddons || [];
  const nights = quoteDetails.nights || 0;
  const sport = request.sport || 'Football';
  const destination = request.destination || 'Spain';
  const tourName = request.tourName || request.contactName || 'Your Club';
  const mstStaff = Number(request.staffCount) || 1;
  const costPerPerson = quoteDetails.costPerPersonToCustomer || 0;
  const payingPlayers = quoteDetails.payingPlayers ?? (Number(request.participants) || 0);
  const totalCost = quoteDetails.totalCostToCustomer ?? 0;

  const fixturesAddon       = addons.find((a) => a.categoryName === 'Fixtures');
  const trainingAddon       = addons.find((a) => a.categoryName === 'Training Sessions');
  const hasPadel            = addons.some((a) => a.categoryName === 'Padel');
  const numFixtures         = fixturesAddon?.quantity || (fixturesAddon ? 1 : 0);
  const numTrainingSessions = trainingAddon?.quantity || (trainingAddon ? 1 : 0);

  const quoteDate         = new Date().toISOString().slice(0, 10);
  const depositDueDate    = addDays(quoteDate, 14);
  const finalDueDate      = subtractMonths(request.departureDate, 2);
  const instalment2Date   = midDateStr(depositDueDate, finalDueDate);
  const depositAmt        = payingPlayers * 100;
  const remaining         = Math.max(0, totalCost - depositAmt);
  const instalment2Amt    = Math.round(remaining * 0.5 * 100) / 100;
  const instalment3Amt    = Math.round((remaining - instalment2Amt) * 100) / 100;

  const fd = request.flightDetails || {};
  const isMst = request.brand !== 'your_tour';
  const company = isMst ? 'MST Sports Tours' : 'Your Tour';
  const sym = CURRENCY_SYMBOLS[request.currency] || '£';
  const breakdown = quoteDetails.breakdown || [];
  const hasFlights = !isMst || breakdown.some((r) => /flight/i.test(r.categoryName || ''));
  const hasMatchTickets = !isMst || addons.some((a) => a.categoryName === 'Match Tickets');

  let y = 0;

  // ── Header ────────────────────────────────────────────────────────────────
  y = isMst
    ? drawMstHeader(doc, logoDataUrl, crestDataUrl, tourName)
    : drawYourTourHeader(doc, logoDataUrl, crestDataUrl, tourName);

  // ── Title ─────────────────────────────────────────────────────────────────
  doc.setFont('helvetica', 'bolditalic');
  doc.setFontSize(20);
  setColor(doc, BLACK);
  const title = isMst ? 'Tour Confirmation' : 'Your Tour Confirmation';
  doc.text(title, pageW / 2, y, { align: 'center' });
  // Underline
  const tw = doc.getTextWidth(title);
  setDraw(doc, BLACK);
  doc.setLineWidth(0.5);
  doc.line((pageW - tw) / 2, y + 1, (pageW + tw) / 2, y + 1);
  y += 10;

  // ── Salutation ────────────────────────────────────────────────────────────
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10.5);
  setColor(doc, BLACK);
  doc.text('Dear Parent / Carer,', marginL, y);
  y += 8;

  // ── Paragraph 1 ──────────────────────────────────────────────────────────
  const p1 = `We are delighted to inform you that ${tourName} has secured dates for our ${destination} ${sport} Experience and your child is now invited to register their interest for one of the limited spaces available.`;
  doc.setFontSize(10);
  setColor(doc, BLACK);
  const p1lines = doc.splitTextToSize(p1, contentW);
  doc.text(p1lines, marginL, y);
  y += p1lines.length * 5.2 + 5;

  // ── Paragraph 2 ──────────────────────────────────────────────────────────
  const p2 = `Following conversations with club representatives, ${company} has designed a bespoke itinerary which we believe will provide your child and their teammates with a truly unforgettable experience.`;
  const p2lines = doc.splitTextToSize(p2, contentW);
  doc.text(p2lines, marginL, y);
  y += p2lines.length * 5.2 + 6;

  // ── What will your child enjoy? ───────────────────────────────────────────
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10.5);
  doc.text('What will your child enjoy?', marginL, y);
  y += 6;

  const mstBullets = isMst ? buildMstIncludedBullets(request, { matchTicketBullet: false }) : null;
  const bullets = isMst ? (mstBullets.length ? mstBullets : ['Full itinerary to be confirmed']) : [
    nights > 0 ? `${nights} Night${nights !== 1 ? 's' : ''} Accommodation at Your Tour ${destination} Hotel` : null,
    numFixtures > 0 ? `${numFixtures} Competitive Fixture${numFixtures !== 1 ? 's' : ''}` : null,
    numTrainingSessions > 0 ? `${numTrainingSessions} UEFA Licensed Training Session${numTrainingSessions !== 1 ? 's' : ''}` : null,
    'x1 Live Match Ticket — Game TBC',
    'x1 EPL Stadium Tour — Elche/Valencia or Levante',
    hasPadel ? 'x1 Padel Pairs Tournament' : null,
    'x1 Indoor Pool & Beach Time',
    'Yoga and other Football Related Activities',
    `${mstStaff} Designated Bi-Lingual Tour Leader${mstStaff !== 1 ? 's' : ''} for Duration of Itinerary`,
    'Transport for Duration of Itinerary',
    'Return Transport from school to Manchester Airport',
    buildMealPlan(request).bulletText,
  ].filter(Boolean);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  setColor(doc, BLACK);
  bullets.forEach((bullet) => {
    const lines = doc.splitTextToSize(bullet, contentW - 6);
    if (y + lines.length * 5 > pageH - 15) { doc.addPage(); y = 20; }
    doc.text('•', marginL + 1, y);
    doc.text(lines, marginL + 5, y);
    y += lines.length * 5 + 1.5;
  });
  y += 5;

  // ── Cost and dates ────────────────────────────────────────────────────────
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10.5);
  doc.text('What is the cost and when are the dates?', marginL, y);
  y += 7;

  doc.setFontSize(10);

  // Flights included line (MST: a plain price line when the quote has no flights)
  const flightsLabel = hasFlights ? 'Flights Included: ' : 'Price: ';
  const flightsValue = hasFlights
    ? `${fmt(costPerPerson, sym)}  (Flight price quoted on ${formatDate(quoteDate)})`
    : `${fmt(costPerPerson, sym)} per person`;
  doc.setFont('helvetica', 'bold');
  doc.text(flightsLabel, marginL, y);
  const flightsLabelW = doc.getTextWidth(flightsLabel); // capture width in bold before switching
  doc.setFont('helvetica', 'normal');
  doc.text(flightsValue, marginL + flightsLabelW, y);
  y += 6;

  // Check in / out (bold + underline)
  const checkLine = `Check in: ${formatDate(request.departureDate)} | Check out: ${formatDate(request.returnDate)}`;
  doc.setFont('helvetica', 'bold');
  doc.text(checkLine, marginL, y);
  setDraw(doc, BLACK);
  doc.setLineWidth(0.3);
  doc.line(marginL, y + 0.8, marginL + doc.getTextWidth(checkLine), y + 0.8);
  y += 6;

  // Depart / Return + baggage — only when the tour has flights
  if (hasFlights) {
    const returnArrival = fd.returnArrivalTime ? ` | Arrives ${fd.returnArrivalTime}` : '';
    const departLine = `Depart ${fd.outboundFlight || '—'} ${fd.outboundTime || ''} / Return: ${fd.returnFlight || '—'} ${fd.returnTime || ''}${returnArrival}`.trim();
    doc.setFont('helvetica', 'normal');
    const departLines = doc.splitTextToSize(departLine, contentW);
    doc.text(departLines, marginL, y);
    y += departLines.length * 5.2 + 4;

    // Baggage (bold)
    doc.setFont('helvetica', 'bold');
    doc.text('Baggage Allowance: 10 kg Cabin Bag plus small backpack', marginL, y);
    y += 8;
  } else {
    y += 2;
  }

  // ── Added Costs ───────────────────────────────────────────────────────────
  // ~100mm needed from here through the end of "Secure Your Tour Today" and
  // the footer — this content never auto-paginates otherwise, so a long
  // group name or extra line-wrapping earlier on the page can push it clean
  // off the bottom.
  if (y > pageH - 100) { doc.addPage(); y = 20; }

  doc.setFont('helvetica', 'bold');
  doc.text('Added Costs', marginL, y);
  setDraw(doc, BLACK);
  doc.setLineWidth(0.3);
  doc.line(marginL, y + 0.8, marginL + doc.getTextWidth('Added Costs'), y + 0.8);
  y += 6;

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  const insuranceText = 'Personal travel insurance for all travellers — between £8–10 per person — to be shared with designated school contact 2 weeks before travel.';
  const insuranceLines = doc.splitTextToSize(insuranceText, contentW - 6);
  doc.text('•', marginL + 1, y);
  doc.text(insuranceLines, marginL + 5, y);
  y += insuranceLines.length * 5 + 4;

  if (hasMatchTickets) {
    const comp = request?.market === 'spain_to_uk' ? 'Premier League' : 'La Liga';
    const laLigaText = `${comp} Match Ticket — The ticket for the ${comp} match is not included in the price above. ${comp} fixture dates are not confirmed until approximately 2 weeks before the fixture date. Once confirmed, your organiser will receive an email advising the date, time, and price. A separate one-off invoice will then be issued, payable within 7 days, so that tickets can be purchased.`;
    const laLigaLines = doc.splitTextToSize(laLigaText, contentW - 6);
    doc.text('•', marginL + 1, y);
    doc.text(laLigaLines, marginL + 5, y);
    y += laLigaLines.length * 5 + 6;
  } else {
    y += 2;
  }

  // ── Secure Your Tour Today ────────────────────────────────────────────────
  if (y > pageH - 60) { doc.addPage(); y = 20; }

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10.5);
  doc.text(isMst ? 'Secure Your Place Today!' : 'Secure Your Tour Today!', marginL, y);
  y += 6;

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  const securePre = 'Places are strictly limited and offered on a first come first served basis. To secure a place for your child and you (or a family member), simply return ';
  const securePreLines = doc.splitTextToSize(securePre, contentW);
  doc.text(securePreLines, marginL, y);
  y += securePreLines.length * 5.2;

  doc.setFont('helvetica', 'bold');
  const amtText = `${sym}100.00`;
  doc.text(amtText, marginL, y);
  doc.setFont('helvetica', 'normal');
  const afterAmt = ` per person to your designated club contact before `;
  doc.text(afterAmt, marginL + doc.getTextWidth(amtText), y);
  doc.setFont('helvetica', 'bold');
  doc.text(formatDate(depositDueDate), marginL + doc.getTextWidth(amtText) + doc.getTextWidth(afterAmt), y);
  y += 7;

  const perPerson2 = payingPlayers > 0 ? Math.round((instalment2Amt / payingPlayers) * 100) / 100 : 0;
  const perPerson3 = payingPlayers > 0 ? Math.round((instalment3Amt / payingPlayers) * 100) / 100 : 0;

  doc.setFont('helvetica', 'normal');
  doc.text(`2nd deposit — ${fmt(perPerson2, sym)} due ${formatDate(instalment2Date)}`, marginL, y);
  y += 5.5;
  doc.text(`Final deposit — ${fmt(perPerson3, sym)} due ${formatDate(finalDueDate)}`, marginL, y);
  y += 7;

  doc.setFontSize(9.5);
  setColor(doc, GRAY);
  doc.text(`${company} welcomes your questions and contact details are at the head of this page.`, marginL, y);

  // ── Footer ────────────────────────────────────────────────────────────────
  if (isMst) drawMstFooter(doc, y, logoDataUrl);
  else drawYourTourFooter(doc, y, logoDataUrl);

  return doc.output('datauristring').split(',')[1];
}
